import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import BaseModal from "../modal/base/BaseModal";
import Button from "../button";
import { TournamentCancel } from "../../../@apis/v1/tournament.service";
import { putTournamentPayCancel } from "../../../@apis/v1/payment.service";
import type { OnErrorResponse } from "../../../@apis/dto/response/common.response";
import { useModalStore } from "../../../lib/store/useModalStore";

const CANCEL_REASONS = [
  "개인 사정",
  "일정 변경",
  "참가 불가",
  "다른 대회 참가",
  "교통/거리 문제",
  "기타(직접 입력)",
] as const;

const ETC_REASON = "기타(직접 입력)";

type Props = {
  close: () => void;
  tournamentId: string | number;
  applyId?: string | number;
  // 가상계좌 결제대기 취소는 결제 취소 API(cancelReason 전달)를 사용한다.
  isVbank?: boolean;
  onSuccess?: () => void;
};

export default function TournamentCancelDialog({
  close,
  tournamentId,
  applyId,
  isVbank = false,
  onSuccess,
}: Props) {
  const { openModal } = useModalStore();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [etcText, setEtcText] = useState("");

  const isEtc = reason === ETC_REASON;
  // 취소 사유 선택 필수, 기타는 직접 입력 필수
  const canSubmit = !!reason && (!isEtc || etcText.trim().length > 0);

  const { mutate, isPending } = useMutation({
    mutationFn: (cancelReason: string) =>
      isVbank && applyId != null
        ? putTournamentPayCancel(applyId, { cancelReason })
        : TournamentCancel(tournamentId, cancelReason),
    onSuccess: () => {
      close();
      onSuccess?.();
    },
    // 서버 거부 사유(회차 접수 마감 P144, 취소 기간 만료 등)를 그대로 보여준다 — 일반 문구로 가리지 않는다
    onError: (err: OnErrorResponse) => {
      openModal("ALERT_DIALOG", {
        title: "접수 취소 실패",
        content:
          err?.response?.data?.error?.message ?? "잠시 후 다시 시도해 주세요.",
      });
    },
  });

  return (
    <BaseModal className="p-[20px]" size="dialog" close={close}>
      <div className="flex flex-col gap-[30px] items-center">
        <div className="flex flex-col gap-[20px] items-center w-full">
          {/* 안내 문구 */}
          <div className="flex flex-col gap-[10px] items-center text-center">
            <p className="text-base font-semibold text-black">
              접수를 취소할까요?
            </p>
            <p className="text-sm font-medium text-[#8e8e93]">
              접수 취소 사유를 선택해 주세요.
            </p>
          </div>

          {/* 취소 사유 드롭다운 */}
          <div className="w-full flex flex-col gap-[12px]">
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full h-[44px] px-[12px] rounded-[10px] border border-[#e5e5ea] flex items-center justify-between text-sm"
              >
                <span className={reason ? "text-black" : "text-[#8e8e93]"}>
                  {reason ?? "취소 사유 선택"}
                </span>
                <span
                  className={`text-[#8e8e93] transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                >
                  ⌄
                </span>
              </button>
              {open && (
                <ul className="absolute z-10 mt-[4px] w-full bg-white border border-[#e5e5ea] rounded-[10px] overflow-hidden shadow-lg">
                  {CANCEL_REASONS.map((r) => (
                    <li key={r}>
                      <button
                        type="button"
                        onClick={() => {
                          setReason(r);
                          setOpen(false);
                        }}
                        className="w-full px-[12px] py-[10px] text-left text-sm text-black hover:bg-[#f2f2f7]"
                      >
                        {r}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 기타(직접 입력) */}
            {isEtc && (
              <div className="flex flex-col gap-[4px] items-end">
                <p className="text-[11px] font-medium text-[#8e8e93]">
                  <span className="text-black">{etcText.length}</span>/100
                </p>
                <textarea
                  value={etcText}
                  onChange={(e) => setEtcText(e.target.value.slice(0, 100))}
                  maxLength={100}
                  placeholder="취소 사유를 입력해 주세요."
                  className="w-full h-[80px] p-[12px] rounded-[10px] border border-[#e5e5ea] text-sm resize-none focus:outline-none focus:border-[#007aff]"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-[8px] justify-center">
          <Button
            className="min-w-[100px] font-bold"
            variant="white"
            label="취소"
            onClick={close}
          />
          <Button
            className="min-w-[100px] font-bold"
            variant={canSubmit ? "black" : "disabled"}
            label="확인"
            disabled={!canSubmit || isPending}
            onClick={() => {
              if (!canSubmit || isPending) return;
              const cancelReason = isEtc ? etcText.trim() : reason;
              if (!cancelReason) return;
              mutate(cancelReason);
            }}
          />
        </div>
      </div>
    </BaseModal>
  );
}
